import { useParams, Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/sections/PageHero";
import { useService, useServices } from "@/hooks/useData";
import { servicePath } from "@/lib/content";
import { Reveal, ImageReveal, MaskedLines } from "@/components/motion/Reveal";
import { Button } from "@/components/motion/Button";
import { Process } from "@/components/sections/Process";
import { FAQ } from "@/components/sections/FAQ";
import { QuoteSection } from "@/components/sections/QuoteSection";
import { FinalCta } from "@/components/sections/FinalCta";
import NotFound from "@/pages/NotFound";

const PRESET = {
  privatreinigung: { object_type: "Wohnung", cleaning_types: ["Unterhaltsreinigung"] },
  unterhaltsreinigung: { cleaning_types: ["Unterhaltsreinigung"] },
  bueroreinigung: { object_type: "Büro", cleaning_types: ["Unterhaltsreinigung"] },
  umzugsreinigung: { object_type: "Wohnung", cleaning_types: ["Umzugsreinigung"] },
  baureinigung: { object_type: "Baustelle", cleaning_types: ["Baureinigung"] },
  fensterreinigung: { cleaning_types: ["Fensterreinigung"] },
  "treppenhaus-liegenschaften": { object_type: "Treppenhaus", cleaning_types: ["Unterhaltsreinigung"] },
  spezialreinigung: { cleaning_types: ["Spezialreinigung"] },
  "gartenbau-pflege": { object_type: "Garten / Aussenanlage", cleaning_types: ["Gartenbau & Pflege"] },
};

export default function ServicePage({ slug: fixed }) {
  const params = useParams();
  const slug = fixed || params.slug;
  const { data: s, isLoading, isError } = useService(slug);
  const { data: all = [] } = useServices();
  if (isError) return <NotFound />;
  if (isLoading || !s) return <div className="min-h-screen bg-ink" />;
  const others = all.filter((x) => x.slug !== slug).slice(0, 4);
  const titleLines = s.name.includes(" & ") ? s.name.split(" & ").map((p, i) => (i === 0 ? `${p} &` : p)) : [s.name];

  return (
    <>
      <SEO title={s.name} description={s.short} image={s.hero} path={servicePath(slug)} />
      <PageHero label={`${s.number} · Leistung`} title={titleLines} image={s.hero} imageAlt={s.name} lead={s.short}>
        <Button to="#offerte" onClick={(e) => { e.preventDefault(); document.getElementById("offerte")?.scrollIntoView({ behavior: "smooth" }); }} variant="light" data-testid="service-cta-offerte">Offerte anfragen</Button>
      </PageHero>

      <section className="bg-paper py-24 md:py-36">
        <div className="container-x grid lg:grid-cols-12 gap-14">
          <Reveal className="lg:col-span-3">
            <p className="label text-mute">Überblick</p>
          </Reveal>
          <div className="lg:col-span-8 lg:col-start-5">
            <MaskedLines as="h2" lines={[s.description.split(". ")[0] + "."]} className="display text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.02]" />
            <Reveal delay={0.2} className="mt-10">
              <p className="text-base md:text-lg text-mute leading-relaxed max-w-2xl">{s.description.split(". ").slice(1).join(". ")}</p>
            </Reveal>
            <Reveal delay={0.3} className="mt-14">
              <p className="label text-mute mb-6">Was dazugehört</p>
              <ul className="border-t border-line">
                {s.benefits.map((b, i) => (
                  <li key={b} data-testid={`service-benefit-${i + 1}`} className="border-b border-line py-5 grid grid-cols-[3rem_1fr] gap-4">
                    <span className="label text-mute pt-1">0{i + 1}</span>
                    <span className="display text-xl md:text-2xl text-ink">{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {s.gallery?.length > 0 && (
        <section className="bg-white py-24 md:py-32" aria-label="Galerie">
          <div className="container-x grid grid-cols-12 gap-4 md:gap-6">
            {s.gallery.map((g, i) => (
              <ImageReveal
                key={g}
                src={g}
                alt={`${s.name} – Detail ${i + 1}`}
                delay={i * 0.1}
                className={`img-zoom ${i === 0 ? "col-span-12 md:col-span-7 aspect-[4/3]" : i === 1 ? "col-span-12 md:col-span-5 aspect-[4/5] md:mt-24" : "col-span-12 md:col-span-8 md:col-start-3 aspect-[16/9]"}`}
                sizes="(min-width:768px) 60vw, 100vw"
              />
            ))}
          </div>
        </section>
      )}

      <Process />
      {s.faq?.length > 0 && <FAQ items={s.faq} title="Fragen zu dieser Leistung." label="FAQ" />}
      <QuoteSection preset={PRESET[slug]} />

      <section className="bg-paper py-24 border-t border-line">
        <div className="container-x">
          <p className="label text-mute mb-8">Weitere Leistungen</p>
          <ul className="border-t border-line">
            {others.map((o) => (
              <li key={o.slug} className="border-b border-line">
                <Link to={servicePath(o.slug)} className="group flex items-baseline gap-5 py-5">
                  <span className="label text-mute group-hover:text-signal transition-colors">{o.number}</span>
                  <span className="display text-2xl md:text-3xl uppercase tracking-[-0.02em] text-ink/70 group-hover:text-ink group-hover:translate-x-2 transition-[transform,color] duration-700 ease-out">{o.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
